import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CartLine from '/Foundation/Common/code/Views/Foundation/Common/CartLine';
import { getCart } from '../Thunks/CartThunks.jsx';
import Spinner from '/Foundation/Common/code/Views/Foundation/Common/Spinner';
import Notification from '/Foundation/Common/code/Views/Foundation/Common/Notifications/Notification';
import { toggleExternalCheckoutMessage, toggleGeneralError, toggleMiniCart } from '../Slices/CartSlice.jsx';
import Sidebar from './Sidebar.jsx';
import { getFormattedPrice, shouldShowCheckoutBtnSpinner } from '/Foundation/Common/code/Utils/Utils';
import AccessoryAddToCartButton from './AccessoryAddToCartButton.jsx';
import { AdobeAnalyticsUtil } from '/Foundation/Common/code/Views/Foundation/Common/AdobeAnalytics/AdobeAnalyticsUtil.js';
import { Analytics_AddToCart } from '/Foundation/Common/code/Views/Foundation/Common/AdobeAnalytics/Analytics_AddToCart';
import { trackCartView } from '../analytics/index.js';
import CommerceApiFetchWrapper from '/Foundation/Common/code/Utils/CommerceApiFetchWrapper';
import { onFocusChangeFocusTo } from '/Foundation/Common/code/Utils/WcagUtils.js';

const Cart = () => {
  const dispatch = useDispatch();
  const slider = useRef();
  const cart = useSelector((state) => state.Cart);
  const cartPageData = useSelector((state) => state.CartPageData);
  const [inventoryData, setInventoryData] = useState({});
  const [checkoutBtnIsLoading, setCheckoutBtnIsLoading] = useState(false);
  let pos = { top: 0, left: 0, x: 0, y: 0 };
  const cartBody = useRef();

  const { lineItems, cartLoaded, showGeneralError, showExternalCheckoutErrorMessage, total, currencyCode } = cart;

  useEffect(() => {
    if (cart.miniCartIsOpen) {
      document.querySelector('html').classList.add('no-scroll');
      document.querySelector('body').classList.add('no-scroll');
      dispatch(getCart());
      trackCartView();
      document.querySelector('.add-to-cart-modal-first-focusable').focus();
    } else {
      document.querySelector('html').classList.remove('no-scroll');
      document.querySelector('body').classList.remove('no-scroll');
    }
  }, [cart.miniCartIsOpen]);

  useEffect(() => {
    if (cartLoaded && cartPageData.accessories?.length > 0) {
      Analytics_AddToCart?.ApplyAddToCartButtonEvent();
    }
  }, [cartLoaded]);

  useEffect(() => {
    var urlParams = new URLSearchParams(window.location.search);
    let showMessage = false;
    if (urlParams.get('error') !== null) {
      showMessage = true;
      dispatch(toggleMiniCart(true));
      dispatch(toggleExternalCheckoutMessage({ showErrorMessage: showMessage }));
    }
  }, []);

  useEffect(() => {
    if (cartLoaded && cartPageData.accessories?.length > 0 && window._THULEDATA?.isB2CEnabled) {
      Analytics_AddToCart?.ApplyAddToCartButtonEvent();
      const payload = (cartPageData.accessories || [])
        .map((acc) => ({
          productId: acc.productId,
          skuIds: [acc.erpOutdoor || acc.productId].filter(Boolean),
        }))
        .filter((p) => p.productId && p.skuIds.length > 0);

      CommerceApiFetchWrapper('/cart/inventory-batch', {
        method: 'POST',
        body: JSON.stringify(payload),
      }).then((response) => {
        const inventoryMap = {};
        response.items?.forEach((item) => {
          inventoryMap[item.productId] = item.isInStock;
        });
        setInventoryData(inventoryMap);
      });
    }
  }, [cartLoaded, cartPageData.accessories]);

  const getErrorMessage = () => {
    var urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('errorType') === 'outOfStock') {
      return cartPageData.externalCheckoutProductOutOfStock;
    }
    return cartPageData.externalCheckoutGeneralError;
  };

  const showErrorMsg = showGeneralError || showExternalCheckoutErrorMessage;
  const isThule = window._THULEDATA.SitecoreSiteName.toLowerCase() === 'thule';

  const mouseDownHandler = function (e) {
    pos = {
      // The current scroll
      left: slider.current.scrollLeft,
      top: slider.current.scrollTop,
      // Get the current mouse position
      x: e.clientX,
      y: e.clientY,
    };

    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
  };

  const mouseMoveHandler = function (e) {
    slider.current.querySelectorAll('.slider-item').forEach((item) => {
      item.style.pointerEvents = 'none';
    });
    // How far the mouse has been moved
    const dx = e.clientX - pos.x;
    const dy = e.clientY - pos.y;

    // Scroll the element
    slider.current.scrollTop = pos.top - dy;
    slider.current.scrollLeft = pos.left - dx;
  };

  const mouseUpHandler = function () {
    slider.current.querySelectorAll('.slider-item').forEach((item) => {
      item.style.pointerEvents = 'auto';
    });
    document.removeEventListener('mousemove', mouseMoveHandler);
    document.removeEventListener('mouseup', mouseUpHandler);

    slider.current.style.cursor = 'grab';
    slider.current.style.removeProperty('user-select');
  };

  const keyboardHandler = (e) => {
    if (e.keyCode === 27) {
      dispatch(toggleMiniCart(false));
      document.querySelector(cart.openBy).focus();
    }
    document.removeEventListener('keydown', keyboardHandler);
  };

  const trackInitiateCheckout = () => {
    let analyticsProducts = [];
    let analyticsData = null;
    try {
      analyticsData = JSON.parse(localStorage.getItem('analytics.categories'));
    } catch (e) {
      console.error('[Analytics] Failed to parse analytics.categories', e);
    }
    cart.lineItems?.map((line) => {
      let analyticsEntry;
      if (analyticsData) analyticsEntry = analyticsData[line.key];
      analyticsProducts.push({
        id: line.sku,
        name: line.webtitle1,
        over_category: analyticsEntry?.Overcategoryanalytics,
        subcategory: analyticsEntry?.Subcategoryanalytics,
        category: analyticsEntry?.Categoryanalytics,
        colour: analyticsEntry?.ColorSpecification,
        stock_status: line.isInStock ? 'In stock' : 'Out of stock',
      });
    });
    const adobeAnalyticsData = {
      cart_type: 'ordinary_cart',
      products: analyticsProducts,
    };

    AdobeAnalyticsUtil.DataLayer.TrackEvent('initiate_checkout', adobeAnalyticsData);
  };

  const sortedAccessories = (cartPageData.accessories || []).slice().sort((a, b) => {
    const aSellable = inventoryData[a.productId] ? 1 : 0;
    const bSellable = inventoryData[b.productId] ? 1 : 0;
    return bSellable - aSellable;
  });

  return (
    <>
      <div
        tabIndex={cart.miniCartIsOpen ? '0' : null}
        onFocus={() => onFocusChangeFocusTo('.add-to-cart-modal-last-focusable')}
      ></div>
      <div
        id="addToCartModal"
        className={`add-to-cart-modal on-top ${cart.miniCartIsOpen ? 'wrapper-visible' : ''}`}
        aria-hidden={cart.miniCartIsOpen ? 'false' : 'true'}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-to-cart-modal-heading"
        onKeyDown={(e) => keyboardHandler(e)}
      >
        <header>
          <div className="add-to-cart-modal-header">
            <div>
              <span id="add-to-cart-modal-heading" className="me-4 heading6">
                {cartPageData.cartHeading}
              </span>
              <span className="text-color--gray80 paragraph--s rpa-cart-quantity">
                {cart.lineItems?.reduce((sum, item) => sum + item.quantity, 0)} {cartPageData.items}
              </span>
            </div>
            <button
              className="btn hide-add-to-cart-modal rpa-close-cart add-to-cart-modal-first-focusable"
              onClick={() => {
                if (cart.openBy) document.querySelector(cart.openBy).focus();
                dispatch(toggleMiniCart(false));
              }}
              aria-describedby="add-to-cart-modal-close-button"
              disabled={!cart.miniCartIsOpen}
            >
              <span>
                <i className="le-icon-x"></i>
              </span>
              <span className="sr-only" id="add-to-cart-modal-close-button">
                Close {cartPageData.cartHeading}
              </span>
            </button>
          </div>
        </header>
        <section className="modal-content-section d-inline-block h-100" ref={cartBody}>
          <div className="add-to-cart-modal-body">
            {showErrorMsg && (
              <div className="mb-4 mt-4 mx-6">
                <Notification
                  Type={'error'}
                  Size={'sm'}
                  Message={showExternalCheckoutErrorMessage ? getErrorMessage() : cartPageData.generalErrorMessage}
                  ShowIcon={true}
                  CloseAction={() => showGeneralError && dispatch(toggleGeneralError(false))}
                />
              </div>
            )}
            <div id="addToCartProducts" className="products mb-7">
              {cartLoaded ? (
                <>
                  {lineItems?.length > 0 ? (
                    <div className="lines" data-component-label="Product listing - cart">
                      {lineItems.map((product) => (
                        <CartLine
                          key={product.id}
                          product={product}
                          isThule={isThule}
                          currencyCode={currencyCode}
                          cartPageData={cartPageData}
                        />
                      ))}
                    </div>
                  ) : (
                    <p
                      className="paragraph mt-4 text-color--gray80"
                      dangerouslySetInnerHTML={{ __html: cartPageData.noProductsMessage }}
                    ></p>
                  )}
                </>
              ) : (
                <>
                  {!showGeneralError && !showExternalCheckoutErrorMessage && (
                    <div className="d-flex justify-content-center mt-12 cart-loading-spinner">
                      <span className="sr-only">Loading...</span>
                      <Spinner />
                    </div>
                  )}
                </>
              )}
            </div>
            {cartPageData.accessories?.length > 0 && lineItems?.length > 0 && (
              <div className="accessories accessories-slider-wrapper mb-6 mb-lg-10">
                <h4 className="heading8 mb-3 px-6">{cartPageData.accessoriesHeading}</h4>
                <section
                  className="accessory-slider px-6"
                  ref={slider}
                  aria-label="Activity slider"
                  onMouseDown={(e) => mouseDownHandler(e)}
                >
                  {sortedAccessories.map((accessory, i) => {
                    let mainImage;
                    if (accessory.images?.length > 0) mainImage = accessory.images[0];
                    const productUrl = `/${window._THULEDATA.SitecoreLanguage.toLowerCase()}/${
                      accessory.productType?.toLowerCase() === 'spare part' ||
                      accessory.productType?.toLowerCase() === 'accessory'
                        ? 'accessories/'
                        : ''
                    }-_-${accessory.pimId}`;
                    return (
                      <div className="d-flex flex-column accessory slider-item" key={i}>
                        <a href={productUrl}>
                          <div className="image-wrapper">
                            {mainImage && mainImage.assetURL && (
                              <img src={mainImage.assetURL} alt={mainImage.assetDescription} />
                            )}
                            <AccessoryAddToCartButton
                              accessory={accessory}
                              isSellable={inventoryData[accessory.productId] || false}
                              ref={cartBody}
                            />
                          </div>
                        </a>
                        <div className="mb-4 mb-lg-9">
                          <a href={productUrl}>
                            <h5 className="heading6 mt-3 mb-0">{accessory.webTitle1}</h5>
                            <div className="price mt-2 paragraph--s">{accessory.priceContainer?.priceFormatted}</div>
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </section>
              </div>
            )}
          </div>
          <Sidebar />
        </section>
        <div className="checkout">
          <div className="total">
            <span>{cartPageData.estimatedTotal}</span> <span>{getFormattedPrice(total, currencyCode)}</span>
          </div>
          <button
            onClick={() => {
              if (shouldShowCheckoutBtnSpinner()) setCheckoutBtnIsLoading(true);
              trackInitiateCheckout();
              window.location.href = cartPageData.checkoutPage;
            }}
            className={`btn thule-btn-default w-100 rpa-checkout-button ${lineItems.length === 0 ? '' : 'add-to-cart-modal-last-focusable'}`}
            disabled={lineItems.length === 0}
          >
            <span>{cartPageData.checkout}</span>
            {checkoutBtnIsLoading && <Spinner />}
          </button>
        </div>
      </div>
      <div
        id="addToCartModalBackdrop"
        className={`add-to-cart-modal-left-slide-in-backdrop ${cart.miniCartIsOpen ? 'active' : ''}`}
        onClick={() => dispatch(toggleMiniCart(false))}
        tabIndex={cart.miniCartIsOpen ? '0' : null}
        onFocus={() => onFocusChangeFocusTo('.add-to-cart-modal-first-focusable')}
      ></div>
    </>
  );
};

export default Cart;
